import type { TFunction } from 'i18next'
import { useSelector } from 'react-redux'
import { useState } from 'react'

import { SeoPreviewPanel } from './SeoPreviewPanel'
import { findDeep } from '../../../utils/immutableTreeHelpers'
import AddCategoryPanel from './AddCategoryPanel'
import FormGroup from '../../formComponents/FormGroup'
import InfoGroup from '../../formComponents/InfoGroup'
import InputField from '../../formComponents/InputField'
import PageModalCategoryManagement from '../PageModalCategoryManagement'
import RadioButtonField from '../../formComponents/RadioButtonField'
import SelectPageLevelPanel from './SelectPageLevel/SelectPageLevelPanel'

export function isStartpage(page: ImmutableMap): boolean {
  return page.get('href') === '/'
}

type PageEditPanelProps = {
  isBeyondShop: boolean
  mode: 'create' | 'edit' | 'duplicate'
  tModal: TFunction
  changedPage: ImmutableMap
  page: ImmutableMap
  categories: ImmutableList
  maybeGenerateNewSlug: (oldPageSlug: string, title: string) => string
  mainNavigation: ImmutableList
}

export default function PageEditPanel({
  categories,
  changedPage,
  isBeyondShop,
  mainNavigation,
  maybeGenerateNewSlug,
  mode,
  page,
  tModal,
}: Readonly<PageEditPanelProps>) {
  const [showSeoForm, setShowSeoForm] = useState(false)

  const shopTitle = useSelector<State, string>((state) => state.getIn(['shop', 'title']))
  const shopLocale = useSelector<State, string>((state) => state.getIn(['shop', 'locale']))
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))
  const shopDefaultLocale = useSelector<State, string>((state) => state.getIn(['shop', 'defaultLocale']))

  const renderCheckoutInfo = () => {
    return (
      changedPage.get('type') === 'legal' &&
      !changedPage.get('isVisible') && (
        <div key="notification" className="ep-notification-info ep-form-row">
          <p className="ep-form-row-text">
            {tModal('checkoutInfoWhenHidden.message') + ' '}
            <a
              className="ep-form-row-text-link"
              href={`${mboBaseUrl}?ViewAction=UnityMBO-ViewBasketText`}
              target="_parent"
            >
              {tModal('checkoutInfoWhenHidden.checkoutSettingsLink')}
            </a>
          </p>
        </div>
      )
    )
  }

  const renderSeoPanel = () => {
    return (
      mode === 'edit' &&
      page.get('type') !== 'legal' && (
        <SeoPreviewPanel
          showSeoSettingsForm={showSeoForm}
          handleEditClick={() => setShowSeoForm(true)}
          {...{
            shopLocale,
            shopDefaultLocale,
            shopTitle,
            changedPage,
            page,
            maybeGenerateNewSlug,
          }}
        />
      )
    )
  }

  const renderAddCategoryPanel = () => {
    return mode === 'create' && isBeyondShop && <AddCategoryPanel t={tModal} {...{ categories }} />
  }

  const renderPageModalCategoryManagement = () => {
    return (
      changedPage.get('type') === 'category' && (
        <PageModalCategoryManagement
          key="categoryManagement"
          // adminUrl does not include a potential sessionID so we use the
          // `mboBaseUrl` (which does) and the `objectPath` part of the `adminUrl`
          adminUrl={`${mboBaseUrl}?${changedPage.get('adminUrl').split('?')[1]}`}
        />
      )
    )
  }

  const parentPage = findDeep(mainNavigation, (node) => node.get('slug') === changedPage.get('pageParentSlug'))

  const parentIsInvisible =
    (changedPage.get('parents') &&
      changedPage.get('parents').count() > 0 &&
      changedPage.get('parents').last().get('isVisible') === false &&
      changedPage.get('parents').last().get('navigation') === changedPage.get('navigation')) ||
    parentPage?.get('isVisible') === false

  const explanation =
    (parentIsInvisible && 'childOfHiddenPageNotShowableExplanation') ||
    (isStartpage(changedPage) && 'startpageNotHideableExplanation')

  const visibilityDisabledInfo = tModal(`visibilitySection.${explanation}`)

  const radioButtons = [
    {
      label: tModal('visibilitySection.showRadioButton.label'),
      value: true,
    },
    {
      label: tModal('visibilitySection.hideRadioButton.label'),
      value: false,
    },
  ]

  const navigationButtons = [
    {
      label: tModal('navigationSection.mainMenuRadioButton.label'),
      value: 'main',
    },
    {
      label: tModal('navigationSection.footerMenuRadioButton.label'),
      value: 'footer',
    },
  ]

  const radioButtonInfoGroup = (
    <InfoGroup info={tModal('visibilitySection.hideRadioButton.explanation')}>
      <RadioButtonField name="isVisible" buttons={radioButtons} />
    </InfoGroup>
  )

  return (
    <>
      {!isBeyondShop && renderCheckoutInfo()}
      <FormGroup
        key="pageName"
        tagName="label"
        name="title"
        label={tModal('pageNameInputField.label') + '*'}
        title={tModal('pageNameInputField.tooltip')}
      >
        <InputField autoFocus name="title" type="text" maxLength={100} disabled={changedPage.get('type') === 'legal'} />
      </FormGroup>

      {renderAddCategoryPanel()}
      {mode !== 'duplicate' && (
        <>
          <FormGroup key="visibility" name="isVisible" label={tModal('visibilitySection.title')}>
            {
              // allow the user to unhide the startpage if it is hidden. Display notification otherwise
              (changedPage.get('type') !== 'category' && parentIsInvisible) ||
              (isStartpage(changedPage) && changedPage.get('isVisible')) ? (
                <p className="ep-form-row-text">{visibilityDisabledInfo}</p>
              ) : (
                radioButtonInfoGroup
              )
            }
          </FormGroup>

          {changedPage.get('type') === 'blank' && (
            <div className="ep-form-row">
              <div className="ep-form-row-label">
                <span className="ep-form-row-label-text">{tModal('navigationSection.title')}</span>
                <div>
                  {isStartpage(changedPage) && (
                    <p className="ep-form-row-text">{tModal('navigationSection.startpageNotMovableExplanation')}</p>
                  )}
                  <RadioButtonField disabled={isStartpage(changedPage)} name="navigation" buttons={navigationButtons} />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {!isBeyondShop && changedPage.get('type') === 'category' && !isStartpage(changedPage) && (
        <FormGroup key="visibleInNavigation" name="visibleInNavigation" label={tModal('visibilityInMenuSection.title')}>
          <InfoGroup info={tModal('visibilityInMenuSection.explanation')}>
            <RadioButtonField
              name="visibleInNavigation"
              disabled={!changedPage.get('isVisible')}
              buttons={[
                {
                  label: tModal('visibilityInMenuSection.showRadioButton.label'),
                  value: true,
                },
                {
                  label: tModal('visibilityInMenuSection.hideRadioButton.label'),
                  value: false,
                },
              ]}
            />
          </InfoGroup>
        </FormGroup>
      )}
      {renderPageModalCategoryManagement()}
      {mode === 'create' && changedPage.get('navigation') === 'main' && <SelectPageLevelPanel />}
      {renderSeoPanel()}
    </>
  )
}
